import { useId, useState } from 'react';
import {
  ChosenStep,
  type DocumentChoice,
  type DocumentStage,
  HiddenFileInputs,
  PagesStep,
  SourcesStep,
  useFileInputRefs,
  useNoDocument,
  usePageFlow,
} from './DocumentPaneSteps';

export type { DocumentChoice, DocumentStage };

/**
 * The left pane of «Nuova spesa» (design §1.3 steps 1–2, §1.4): the three sources or no document,
 * then, for photos, the pages: each opens the Immagini editor, they can be reordered, added and
 * removed, and «Crea il PDF» merges them in order into one A4 PDF.
 */
export function DocumentPane({
  choice,
  onChoice,
  onStage,
  onContinue,
  replaceable,
}: {
  readonly choice: DocumentChoice;
  readonly onChoice: (choice: DocumentChoice) => void;
  readonly onStage: (stage: DocumentStage) => void;
  /** Phone: back to the fields when the choice is already made. */
  readonly onContinue: () => void;
  /** Edit mode: the stored document can be replaced, or kept. */
  readonly replaceable: boolean;
}) {
  const uid = useId();
  const refs = useFileInputRefs();
  /** Choosing again: the sources show even though a choice is made. */
  const [choosing, setChoosing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const flow = usePageFlow(onChoice, onStage, () => setChoosing(false));
  const { pages, editing, problem, creating, setEditing, updatePages, pick, onDrop } = flow;
  const noDoc = useNoDocument(onChoice, () => setChoosing(false));

  const inputs = <HiddenFileInputs refs={refs} pick={pick} />;

  if (pages.length > 0) {
    return (
      <PagesStep
        {...{ uid, inputs, pages, problem, creating, editing }}
        onEdit={setEditing}
        onCloseEditor={() => setEditing(null)}
        onMove={flow.move}
        onRemove={(index) => updatePages(pages.filter((_, i) => i !== index))}
        onAdd={() => refs.gallery.current?.click()}
        onCreate={() => void flow.createPdf()}
        onCancel={() => updatePages([])}
        onPageDone={(next) => {
          updatePages(pages.map((p, i) => (i === editing ? next : p)));
          setEditing(null);
        }}
      />
    );
  }

  if (choice.kind !== 'unset' && !choosing) {
    return (
      <ChosenStep
        uid={uid}
        choice={choice}
        replaceable={replaceable}
        onChange={() => setChoosing(true)}
        onContinue={onContinue}
      />
    );
  }

  return (
    <SourcesStep
      {...{ uid, inputs, problem, choosing, dragging, ...noDoc }}
      refs={refs}
      onDrag={setDragging}
      onDrop={onDrop}
      onKeep={() => setChoosing(false)}
    />
  );
}
